import { useState, type JSX } from 'react';
import { usePref } from '../lib/prefs.js';
import { Button } from '../components/Button.js';
import { SettingsRow } from '../components/Settings.js';
import { Switch } from '../components/Switch.js';
import { forgetNewMemory, newBadgePrefKey } from './newRecords.js';

/**
 * Ligne de reglage "signaler les nouveautes" d'un ecran, fournie par le SOCLE.
 *
 * Le hub Parametres la pose dans « Mon affichage » de chaque porte (`ReglagesDeclares`). Meme raison d'etre que `ViewDefaultSetting` : aucune app ne recode
 * ce reglage, les 40 apps a venir posent la meme ligne, avec les memes mots et
 * le meme comportement. Un reglage de socle qui serait recopie dans chaque app
 * finirait par dire 40 choses differentes de la meme mecanique.
 *
 * CE QU'ELLE COMMANDE : la pastille "Nouveau" des vues de collection. Une
 * fiche creee apres la premiere visite de la porte et jamais ouverte sur ce
 * poste se voit au premier coup d'oeil, et s'eteint des qu'on l'ouvre (modele
 * de la boite mail). Le calcul vit dans le core (`project.ts`), la memoire
 * dans `newRecords.ts` : ici, on n'a que l'interrupteur et le rattrapage.
 *
 * ALLUMEE PAR DEFAUT : cle absente = allumee. Un utilisateur qui n'a jamais
 * ouvert les reglages doit voir ses nouveautes.
 *
 * LE RATTRAPAGE ("Tout marquer comme lu") n'est pas un confort : une liste qui
 * a pris 200 entrees d'un coup (import, connecteur, formulaire public) ne se
 * rattraperait qu'en ouvrant 200 fiches. Il vaut pour TOUTES les portes de
 * l'ecran, parce qu'un reglage ne sait pas quelle porte l'utilisateur
 * regardait, et pour tous les ECRANS qu'on lui nomme (`screenIds`), parce que
 * l'interrupteur, lui, vaut pour l'app entiere.
 *
 * UNE SEULE LIGNE PAR APP. L'interrupteur vaut pour l'APP entiere (c'est un
 * choix de confort, pas un reglage d'affichage) : en poser deux ferait deux
 * interrupteurs jumeaux de la meme pref, qui basculeraient ensemble sans que
 * rien ne l'explique. La memoire, elle, est par porte donc par ecran : c'est
 * `screenIds` qui reconcilie les deux, en nommant tous les ecrans que le
 * rattrapage doit nettoyer.
 *
 * @example
 * <SettingsSection title="Mon affichage">
 *   <NewBadgeSetting appId="contact" screenId="contacts" />
 * </SettingsSection>
 *
 * @example App a deux ecrans de collection : le rattrapage les sert tous les deux.
 * <NewBadgeSetting
 *   appId="mon-app"
 *   screenId="tickets"
 *   screenIds={['tickets', 'work-orders']}
 * />
 */
export interface NewBadgeSettingProps {
  /** Id de l'app, identique a celui passe a `useScreen`. */
  appId: string;
  /** Id de l'ecran, identique a celui passe a `useScreen`. */
  screenId: string;
  /**
   * LES ECRANS QUE LE RATTRAPAGE DOIT NETTOYER. Defaut : `[screenId]`.
   *
   * L'INTERRUPTEUR vaut pour l'APP entiere (une seule cle,
   * `app.<appId>.nouveau`), mais la MEMOIRE est par app + ecran + porte : une
   * app a deux ecrans de collection ne pose qu'une ligne de reglage, et "Tout
   * marquer comme lu" ne nettoyait alors qu'un ecran sur deux, sans que rien ne
   * l'explique. Une app multi-ecrans passe donc ici TOUS ses ids d'ecran de
   * collection.
   *
   * POURQUOI PAS UN BALAYAGE PAR APP, ce qui rendrait cette prop inutile : le
   * prefixe `app.<appId>.` attraperait les favoris, les vues et toutes les
   * autres prefs d'ecran. Le suffixe de nature (`.nouveau`) ne suffit pas a
   * rendre ce balayage sur, et une pref effacee par erreur ne se rattrape pas.
   */
  screenIds?: readonly string[];
  /** Libelle de la ligne. Defaut : "Signaler les nouveautes". */
  label?: string;
  /** Precision sous le libelle. Un defaut parlant est fourni. */
  hint?: string;
}

export function NewBadgeSetting({
  appId,
  screenId,
  screenIds,
  label = 'Signaler les nouveautés',
  // LA PHRASE EXACTE, et pas une promesse plus large que le mecanisme. Deux
  // conditions, toutes les deux vraies : arrivee APRES la premiere visite de
  // cet ecran (ce qui existait avant n'est pas une nouveaute, c'est le fond de
  // la collection) ET jamais ouverte. Le hint d'avant ne disait que la seconde,
  // donc il promettait une pastille sur des fiches qui n'en portent jamais.
  hint = "Pastille sur les fiches non ouvertes",
}: NewBadgeSettingProps): JSX.Element {
  const [pref, setPref] = usePref<boolean>(newBadgePrefKey(appId), true);
  // Seul un `false` franc eteint : cle absente ou valeur abimee = allumee.
  const enabled = pref !== false;

  /** Les ecrans a nettoyer : ceux qu'on nous donne, sinon celui de la ligne. */
  const cibles = screenIds ?? [screenId];

  /**
   * Le rattrapage vient d'etre fait : il n'y a plus RIEN a marquer, et le
   * bouton doit le dire au lieu de proposer un geste sans effet. L'etat vit le
   * temps du panneau : revenir plus tard, c'est potentiellement avoir de
   * nouveau des nouveautes, et le savoir exigerait de charger les donnees de
   * l'app, ce qu'un panneau de reglages ne fait pas.
   */
  const [done, setDone] = useState(false);

  function toggle(next: boolean): void {
    setPref(next);
    // On ne touche PAS a la memoire en eteignant : rallumer doit retrouver ce
    // qu'on avait deja lu, pas rallumer tout ce qu'on avait ouvert.
    setDone(false);
  }

  const marquerLu = (): void => {
    const resultats = cibles.map(id => forgetNewMemory(appId, id));
    setDone(resultats.every(ecrites => ecrites !== null));
  };
  return (
    <SettingsRow label={label} hint={hint}>
      <Switch
        checked={enabled}
        onChange={toggle}
        /* De l'APP, pas de l'ecran : une seule cle `app.<appId>.nouveau`. */
        aria-label={`Signaler les nouveautés de l’app ${appId}`}
      />
      <Button
        variant="secondary"
        size="sm"
        disabled={!enabled || done}
        title={!enabled ? 'Repérage désactivé' : undefined}
        onClick={marquerLu}
      >
        {done ? 'Tout est lu' : 'Tout marquer comme lu'}
      </Button>
      {done ? <span className="sr-only" role="status">Toutes les fiches sont marquées comme lues.</span> : null}
    </SettingsRow>
  );
}
